/**
 * @fileoverview Progress component for the Storyteller web application.
 * Displays a progress indicator for the current question.
 *
 * @example
 * // Basic usage
 * <Progress />
 */

import { useContext, useEffect, useState } from 'react'
import { DataContext } from '../../DataContext'
import { MiddlewareContext } from '../../MiddlewareContext'
import css from './Progress.module.css'


/**
 * @function Progress
 * @description Component for displaying a progress indicator for the current question.
 * @param {React.HTMLAttributes<HTMLDivElement>} props - The props for the Progress component.
 * @returns {JSX.Element | null} The Progress component.
 */
function Progress(props: React.HTMLAttributes<HTMLDivElement>) {
    const items = []

    const { question } = useContext(MiddlewareContext)
    const { standalone } = useContext(DataContext)

    // If standalone, don't show progress bar
    if (standalone) {
        return null
    }

    if (question) {
        const { total, index } = question

        for (let i = 0; i <= total; i++) {
            items.push(
                <div
                    key={`item-${i}`}
                    data-testid="progress-item"
                    data-active={index === i}
                    className={`${css.item} ${index === i ? css.active : ''}`}
                ></div>
            )
        }
    } else {
        return null
    }

    return <div className={`${props.className} ${css.container}`}>{items}</div>
}

/**
 * @function MobileProgress
 * @description Component for displaying a progress indicator for the current question on mobile devices.
 * @returns {JSX.Element | null} The MobileProgress component.
 */
export function MobileProgress() {
    const [width, setWidth] = useState<number>(window.innerWidth)
    useEffect(() => {
        function handleResize() {
            setWidth(window.innerWidth)
        }

        window.addEventListener('resize', handleResize)

        return () => {
            window.removeEventListener('resize', handleResize)
        }
    }, [])

    if (width >= 768) {
        return null
    }

    return <Progress className={css.mobile} />
}

/**
 * @function DesktopProgress
 * @description Component for displaying a progress indicator for the current question on desktop devices.
 * @returns {JSX.Element | null} The DesktopProgress component.
 */
export function DesktopProgress() {
    const [width, setWidth] = useState<number>(window.innerWidth)
    useEffect(() => {
        function handleResize() {
            setWidth(window.innerWidth)
        }

        window.addEventListener('resize', handleResize)

        return () => {
            window.removeEventListener('resize', handleResize)
        }
    }, [])

    if (width < 768) {
        return null
    }

    return <Progress className={css.desktop} />
}
